import {useCallback} from 'react';
import {
  useIsOnboardingComplete,
  useLogTouchpoint,
  useOnboardingResumeStep,
} from './useTouchpointTracking';

/**
 * Hook for accessing and managing onboarding progress.
 * Uses the touchpoint tracking API instead of localStorage.
 * 
 * The onboarding flow is now tracked as a single touchpoint 'onboarding_flow'.
 * Individual steps (address, username, avatar) are tracked by separate touchpoints
 * logged server-side when those actions complete.
 * 
 * Resume functionality uses individual touchpoints as proxies:
 * - Step 7: home_address_update
 * - Step 8: user_handle_update  
 * - Step 9: profile_picture_update
 * - Step 10: final confirmation step
 */
export const useOnboardingProgress = () => {
  const {isComplete, isLoading: isCompleteLoading} = useIsOnboardingComplete();
  const {
    firstIncompleteStep,
    firstIncompleteStepUrl,
    completedStepCount,
    isLoading: isResumeLoading,
  } = useOnboardingResumeStep();
  const logTouchpointMutation = useLogTouchpoint();

  /**
   * Mark the onboarding flow as complete.
   * This should be called when the user finishes the final step of onboarding.
   */
  const markOnboardingComplete = useCallback(() => {
    logTouchpointMutation.mutate(
      {
        touchpoint: 'onboarding_flow',
        action: 'complete',
      },
      {
        onSuccess: () => {
          console.log('[Onboarding] Successfully marked onboarding as complete');
        },
        onError: (error) => {
          console.error('[Onboarding] Failed to mark onboarding as complete:', error);
        },
      }
    );
  }, [logTouchpointMutation]);

  /**
   * Log that user accessed the onboarding flow
   */
  const logOnboardingAccess = useCallback(() => {
    logTouchpointMutation.mutate({
      touchpoint: 'onboarding_flow',
      action: 'access',
    });
  }, [logTouchpointMutation]);

  // Legacy compatibility methods (no-ops for backward compatibility)
  // These individual step tracking methods are no longer needed since:
  // - Address, username, avatar updates are tracked server-side automatically
  // - We only need to track the overall onboarding_flow completion
  const initializeTracking = useCallback(() => {
    // No-op: initialization is now handled server-side
  }, []);

  /**
   * Log completion of bookmark ahead session
   */
  const logBookmarkAheadSession = useCallback(() => {
    logTouchpointMutation.mutate({
      touchpoint: 'bookmark_ahead_session',
      action: 'complete',
    });
  }, [logTouchpointMutation]);

  const markStepComplete = useCallback((stepIndex: number) => {
    // Individual steps are now tracked server-side via TouchpointTracker
    // This is kept for backward compatibility with existing Onboarding component calls

    // Step 10 (bookmarks) triggers bookmark_ahead_session completion.
    // Step 9 (places) is the new IntroPlaces step; once the backend adds a
    // `bookmark_places_session` touchpoint slug, log it here.
    if (stepIndex === 10) {
      logBookmarkAheadSession();
    }

    // The final step (11) triggers the onboarding_flow completion
    if (stepIndex === 11) {
      markOnboardingComplete();
    }
  }, [markOnboardingComplete, logBookmarkAheadSession]);

  const forceInitialize = useCallback(() => {
    // No-op: no longer need client-side initialization
  }, []);

  const resetProgress = useCallback(() => {
    // No-op: progress is managed server-side
  }, []);

  const isStepComplete = useCallback((stepIndex: number) => {
    // We no longer track individual steps client-side
    // Return false as a safe default
    return false;
  }, []);

  const isLoading = isCompleteLoading || isResumeLoading;
  const totalSteps = 4; // Steps 7, 8, 9, 10

  return {
    // State
    isInitialized: true, // Always initialized with API
    isComplete,
    isLoading,
    firstIncompleteStep,
    firstIncompleteStepUrl,
    completionPercentage: isComplete ? 100 : Math.round((completedStepCount / totalSteps) * 100),
    completedStepCount,
    totalSteps,
    startedAt: null, // Not tracked client-side
    completedAt: null, // Not tracked client-side

    // Actions
    initializeTracking,
    markStepComplete,
    markOnboardingComplete,
    logOnboardingAccess,
    resetProgress,
    forceInitialize,
    isStepComplete,
  };
};

export default useOnboardingProgress;
